// Shared Token Savings Estimator display — used by the project panel, the
// dashboard aggregate cards, and the detailed Observability tab so labels
// and formulas can never diverge between surfaces (Task 7/8).

import { useEffect, useRef, useState } from 'react'
import type { TokenSavingsReport } from '../../api/token-savings'

function formatTokens(n: number): string {
  return n.toLocaleString('en-US')
}

/**
 * Click-to-open explainer popover. Stays open until the user closes it
 * (X button, Escape, or clicking outside) — never auto-dismisses on hover.
 */
function InfoPopover({ label, title, body }: { label: string; title: string; body: string }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <span ref={wrapRef} className="relative inline-block ml-1 align-middle">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Explain: ${label}`}
        aria-expanded={open}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-harbor-surface-raised text-harbor-text-muted text-[10px] hover:text-harbor-text hover:border-harbor-text-muted border border-transparent"
      >
        ?
      </button>
      {open && (
        <span
          role="dialog"
          aria-label={title}
          className="absolute z-20 left-0 top-6 w-80 max-w-[80vw] rounded-xl border border-harbor-border bg-harbor-bg p-4 shadow-xl text-left font-normal"
        >
          <span className="flex items-start justify-between gap-2">
            <strong className="text-sm text-harbor-text">{title}</strong>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close explanation"
              className="text-harbor-text-muted hover:text-harbor-text text-base leading-none px-1"
            >
              ×
            </button>
          </span>
          <span className="block mt-2 text-[13px] leading-relaxed text-harbor-text-secondary">
            {body}
          </span>
        </span>
      )}
    </span>
  )
}

const EXPLAINERS = {
  savingsPct: {
    title: 'Token savings %',
    body: 'Of all the source text your answers drew on (S), this is the share Harbor saved you from loading. If your answers were based on 100 pages but Harbor only sent back 3 pages worth of text, savings are ~97%. It is a share of reading avoided — not a discount on your AI bill, and it does not count the AI thinking or writing tokens.',
  },
  proxyTokens: {
    title: 'Source context avoided (P)',
    body: 'P = S − R. S is the full size of the source documents your answers drew on — what the agent would have had to open and read without Harbor. R is what Harbor actually sent back. P is the difference: the reading the agent skipped.',
  },
  compressionRatio: {
    title: 'Compression ratio',
    body: 'S ÷ R — how many times smaller the delivered answer context was compared to the full source documents behind it. 5× means the agent read one-fifth of what it otherwise would have.',
  },
  returnedTokens: {
    title: 'Returned tokens (R)',
    body: 'The amount of text Harbor actually handed to the agent, counted in tokens (roughly word-pieces). Smaller is better here — it means the agent got what it needed without wading through whole documents.',
  },
} as const

export function TokenSavingsDisclaimer({ text }: { text: string }) {
  return (
    <p
      data-testid="token-savings-disclaimer"
      className="text-xs text-harbor-text-muted bg-harbor-surface-raised/60 border border-harbor-border rounded-lg px-3 py-2"
    >
      {text}
    </p>
  )
}

/**
 * Compact card grid: measured source-context savings on real traffic +
 * coverage. `compact` drops the byProject/byClient/byTool breakdowns for the
 * project/dashboard panels; the Observability tab passes `compact={false}`.
 */
export default function TokenSavingsSummary({
  report,
  compact = true,
}: {
  report: TokenSavingsReport
  compact?: boolean
}) {
  const hasAnyProxyData = report.coverage.eligibleCalls > 0

  return (
    <div className="space-y-4" data-testid="token-savings-summary">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
          <p className="text-xs font-medium text-harbor-text-secondary">
            Token savings
            <InfoPopover
              label="Token savings"
              title={EXPLAINERS.savingsPct.title}
              body={EXPLAINERS.savingsPct.body}
            />
          </p>
          <p className="mt-1 text-2xl font-bold text-emerald-400">
            {report.proxy.savingsPct !== null ? `${report.proxy.savingsPct.toFixed(1)}%` : '—'}
          </p>
          <p className="text-[11px] text-harbor-text-muted mt-0.5">
            {formatTokens(report.proxy.proxyTokens)} tokens avoided
          </p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
          <p className="text-xs font-medium text-harbor-text-secondary">
            Source context avoided
            <InfoPopover
              label="Source context avoided"
              title={EXPLAINERS.proxyTokens.title}
              body={EXPLAINERS.proxyTokens.body}
            />
          </p>
          <p className="mt-1 text-2xl font-bold text-amber-400">
            {formatTokens(report.proxy.proxyTokens)}
          </p>
          <p className="text-[11px] text-harbor-text-muted mt-0.5">Measured on real traffic</p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
          <p className="text-xs font-medium text-harbor-text-secondary">
            Compression ratio
            <InfoPopover
              label="Compression ratio"
              title={EXPLAINERS.compressionRatio.title}
              body={EXPLAINERS.compressionRatio.body}
            />
          </p>
          <p className="mt-1 text-2xl font-bold text-harbor-text">
            {report.proxy.compressionRatio !== null
              ? `${report.proxy.compressionRatio.toFixed(1)}×`
              : '—'}
          </p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-4">
          <p className="text-xs font-medium text-harbor-text-secondary">Coverage</p>
          <p className="mt-1 text-2xl font-bold text-harbor-text">
            {report.coverage.coveragePct !== null
              ? `${report.coverage.coveragePct.toFixed(0)}%`
              : '—'}
          </p>
          <p className="text-[11px] text-harbor-text-muted mt-0.5">
            {report.coverage.measuredCalls}/{report.coverage.eligibleCalls} calls measured
          </p>
        </div>
      </div>

      {!hasAnyProxyData && (
        <p className="text-sm text-harbor-text-muted">
          No instrumented retrieval calls in this window yet.
        </p>
      )}

      {!compact && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BreakdownTable title="By project" rows={report.byProject} />
          <BreakdownTable title="By client" rows={report.byClient} />
          <BreakdownTable title="By tool" rows={report.byTool} />
        </div>
      )}

      <TokenSavingsDisclaimer text={report.disclaimer} />
    </div>
  )
}

function BreakdownTable({ title, rows }: { title: string; rows: TokenSavingsReport['byProject'] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-harbor-border">
      <table className="min-w-full text-sm">
        <thead className="bg-harbor-surface text-harbor-text-secondary text-left">
          <tr>
            <th className="px-3 py-2">{title}</th>
            <th className="px-3 py-2">Tokens avoided</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-harbor-border">
              <td className="px-3 py-2 text-harbor-text">{r.key}</td>
              <td className="px-3 py-2 text-harbor-text-secondary">
                {formatTokens(r.proxyTokens)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={2} className="px-3 py-4 text-harbor-text-muted text-center">
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
